/** Structural subset shared by the repository and client ViewDefinition types. */
export interface OrgArtifactViewDefinitionLike {
  readonly type?: string;
  readonly projection?: string | null;
  readonly projectionKey?: string;
  readonly savedViewId?: string;
  readonly filters?: Record<string, unknown>;
}

const PROTECTED_CANONICAL_PROJECTION_KEYS = new Set(['people:full', 'position:full']);

/**
 * How an Atlas org artifact relates to the folder's canonical people records.
 * The single classifier behind dashboard grouping, card badges, and canvas
 * mode chips — so every surface tells the same story about what a diagram is.
 * See docs/plans/2026-08-03-feat-atlas-jobs-first-workspace-ux-plan.md.
 *
 * - `live`     — a read-only projection of current data (Full Org Chart,
 *                department slices). Always current; per-viewer masking applies.
 * - `position` — the position/staffing planning projection. Where staffing and
 *                succession planning happen; scenarios anchor here.
 * - `planning` — an editable, source-bound scenario workspace. Its Y.Doc owns
 *                collaboration/layout while protected drafts stay server-side.
 * - `editable` — an ordinary org diagram (seeded from data or drawn by hand).
 *                A person's own working surface; can drift from truth.
 * - `custom`   — a saved, named Lens & Display projection. Live like `live`,
 *                but with its own identity, rules, and lifecycle.
 */
export type OrgArtifactKind = 'live' | 'position' | 'planning' | 'editable' | 'custom';

/**
 * Saved People views are live projections but are never the canonical Full
 * Org Chart. Their server-owned rules are referenced only by an opaque ID.
 */
export function isSavedPeopleView(
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined,
): boolean {
  return viewDefinition?.type === 'saved-people-view'
    && viewDefinition.projection === 'people_chart'
    && typeof viewDefinition.savedViewId === 'string'
    && viewDefinition.savedViewId.trim().length > 0;
}

export interface OrgArtifactKindMeta {
  readonly kind: OrgArtifactKind;
  /** i18n key for the badge/label. */
  readonly labelKey: string;
  /** Fallback label when the translation is missing. */
  readonly labelDefault: string;
}

const KIND_META: Record<OrgArtifactKind, OrgArtifactKindMeta> = {
  live: {
    kind: 'live',
    labelKey: 'domainFolder.atlasKind.live',
    labelDefault: 'Live view',
  },
  position: {
    kind: 'position',
    labelKey: 'domainFolder.atlasKind.position',
    labelDefault: 'Position chart',
  },
  planning: {
    kind: 'planning',
    labelKey: 'domainFolder.atlasKind.planning',
    labelDefault: 'Planning',
  },
  editable: {
    kind: 'editable',
    labelKey: 'domainFolder.atlasKind.editable',
    labelDefault: 'Editable',
  },
  custom: {
    kind: 'custom',
    labelKey: 'domainFolder.atlasKind.custom',
    labelDefault: 'Custom view',
  },
};

/**
 * Classify a diagram by its view definition. A `people_chart` projection is a
 * live view; a position chart (by type or projection marker) is the planning
 * projection; anything else is an editable diagram.
 */
export function classifyOrgArtifact(
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined,
): OrgArtifactKind {
  if (isSavedPeopleView(viewDefinition)) return 'custom';
  const projection = viewDefinition?.projection;
  const type = viewDefinition?.type;
  if (type === 'planning-chart' || projection === 'planning_chart') return 'planning';
  if (projection === 'people_chart') return 'live';
  if (
    type === 'position-chart'
    || projection === 'position_chart'
    || projection === 'position_roster'
  ) {
    return 'position';
  }
  return 'editable';
}

export function orgArtifactKindMeta(kind: OrgArtifactKind): OrgArtifactKindMeta {
  return KIND_META[kind];
}

/** True when the diagram is the full-org live projection (not a slice). */
export function isFullOrgLiveView(
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined,
): boolean {
  return (
    viewDefinition?.projection === 'people_chart'
    && viewDefinition?.type === 'people-chart'
  );
}

/** Canonical Atlas projections that may initiate a reviewed live organization change. */
export function isOperationalOrgView(
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined,
): boolean {
  return (
    isFullOrgLiveView(viewDefinition)
    || (
      viewDefinition?.type === 'position-chart'
      && viewDefinition.projection === 'position_chart'
    )
  );
}

/** Auto-generated canonical Atlas projections that must not be archived or purged. */
export function isProtectedCanonicalOrgProjectionDiagram(
  viewDefinition: OrgArtifactViewDefinitionLike | null | undefined,
  folderDomain: string | null | undefined,
): boolean {
  if (!viewDefinition) return false;
  const { projectionKey } = viewDefinition;
  if (
    typeof projectionKey === 'string'
    && PROTECTED_CANONICAL_PROJECTION_KEYS.has(projectionKey)
  ) {
    return true;
  }
  if (folderDomain !== 'orgchart') return false;
  if (
    isFullOrgLiveView(viewDefinition)
    && !viewDefinition.filters?.departmentKey
  ) {
    return true;
  }
  return (
    viewDefinition.type === 'position-chart'
    && viewDefinition.projection === 'position_chart'
  );
}
